import {
	ActionIcon,
	Box,
	createStyles,
	Flex,
	Group,
	Menu,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import {
	useDebouncedValue,
	useDisclosure,
	useMediaQuery,
} from '@mantine/hooks';
import { SelectType } from '@repo/common/components/Selectors';
import { Selector } from '@repo/common/components/Selectors/Selector';
import { SelectorTarget } from '@repo/common/components/Selectors/SelectorTarget';
import { SettingsTitle } from '@repo/common/components/SettingsTypography';
import { DEFAULT_TEMPLATE_ICONS } from '@repo/common/constants/defaultIcons';
import { EntityType } from '@repo/common/enums/entityType';
import { getEntityDisplayType } from '@repo/common/utils/entityDisplayUtils';
import { Button, Icon, Text } from '@repo/foundations';
import { DataTable } from '@repo/mantine-datatable';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef, useState } from 'react';
import type { ITemplate } from '../../../api';
import { queryClient, useQuestionsEnabled } from '../../../api';
import {
	templatesQueryKeyFactory,
	useDeleteTemplate,
	useTemplateList,
	useUpdateTemplate,
} from '../../../api/hooks/templates';
import { openDeleteConfirmModal } from '../../ModalManager/events';
import SearchBox from '../../SearchBox/SearchBox';
import { useTableStyles } from '../../TableV2/TableV2.styles';
import { TeamsTemplateSelector } from './TeamsTemplateSelector';

const useStyles = createStyles((theme) => ({
	icon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 28,
		height: 28,
		borderRadius: theme.radius.sm,
		background: theme.other.getColor('fill/transparent-secondary/default'),
		boxShadow: 'none',
		border: 'none',
		padding: 0,
		cursor: 'default',
	},
}));

interface CreateTemplateType {
	value: EntityType;
	label: string;
}

interface FilterTemplateType {
	value: EntityType | 'all';
	label: string;
}

const BOTTOM_PADDING = 24;
const DEFAULT_INITIAL_DISTANCE = 240;

export const TemplatesListPage = observer(
	({
		navigate,
		setType,
		setTemplateId,
	}: {
		navigate: (path: string) => void;
		setType: (type: EntityType) => void;
		setTemplateId: (templateId: string) => void;
	}) => {
		const { classes, theme } = useStyles();
		const isSmallScreen = useMediaQuery(
			`(max-width: ${theme.breakpoints.sm})`,
			false
		);
		const { classes: tableClasses } = useTableStyles({
			hideCheckbox: false,
			noBorder: true,
			disableSelectedDisplay: true,
			withHover: true,
			stickyColumn: isSmallScreen ? 0 : 1,
		});
		const questionsEnabled = useQuestionsEnabled();
		const [filter, setFilter] = useState<'all' | EntityType>('all');
		const [searchTerm, setSearchTerm] = useState<string>('');

		const createTemplateTypes: CreateTemplateType[] = useMemo(() => {
			const baseTypes = [
				{ value: EntityType.collection, label: 'Collection' },
				{ value: EntityType.column, label: 'Column' },
				{ value: EntityType.dashboard, label: 'Dashboard' },
				{ value: EntityType.document, label: 'Document' },
				{ value: EntityType.glossary, label: 'Glossary term' },
				{ value: EntityType.job, label: 'Job' },
				{ value: EntityType.table, label: 'Table' },
			];

			if (questionsEnabled) {
				baseTypes.splice(6, 0, {
					value: EntityType.question,
					label: 'Question',
				});
			}

			return baseTypes;
		}, [questionsEnabled]);

		const filterTemplateTypes: FilterTemplateType[] = useMemo(() => {
			const baseTypes = [
				{ label: 'All', value: 'all' as const },
				{ label: 'Documents', value: EntityType.document },
				{ label: 'Collections', value: EntityType.collection },
				{ label: 'Glossary terms', value: EntityType.glossary },
			];

			if (questionsEnabled) {
				baseTypes.splice(3, 0, {
					label: 'Questions',
					value: EntityType.question,
				});
			}

			return baseTypes;
		}, [questionsEnabled]);

		const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 200);
		const [isOpen, { toggle: onToggle }] = useDisclosure();
		const tableRef = useRef<HTMLDivElement>(null);

		const { data: templatesRaw } = useTemplateList({
			filters: {
				entity_type_template: filter === 'all' ? undefined : filter,
			},
			options: {
				select: (data) => data?.results || [],
			},
		});

		const templates = useMemo(() => {
			if (!debouncedSearchTerm) return templatesRaw;
			return templatesRaw?.filter((template) =>
				template.title
					?.toLowerCase()
					.includes(debouncedSearchTerm.toLowerCase())
			);
		}, [templatesRaw, debouncedSearchTerm]);

		const tableMaxHeight = (() => {
			const distanceFromTop = tableRef.current
				? tableRef.current.getBoundingClientRect().top
				: DEFAULT_INITIAL_DISTANCE;
			return `calc(100vh - ${distanceFromTop + BOTTOM_PADDING}px)`;
		})();

		const { mutateAsync: updateTemplate } = useUpdateTemplate({
			options: {
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: templatesQueryKeyFactory.list(1),
					});
				},
			},
		});

		const handleTeamChange = (template: ITemplate, value: string[]) => {
			updateTemplate({
				data: { id: template.id, usable_by_teams: value },
			});
		};

		const handleDefaultChange = (template: ITemplate) => {
			updateTemplate({
				data: { id: template.id, is_default: !template.is_default },
			});
		};

		const { mutateAsync: handleDeleteTemplate } = useDeleteTemplate({
			options: {
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: templatesQueryKeyFactory.list(1),
					});
				},
			},
		});

		return (
			<>
				<Box mb="xl">
					<Flex justify="space-between" align="center" mb="3xs">
						<SettingsTitle>Templates</SettingsTitle>
						<Button
							component="a"
							href={
								'https://docs.secoda.com/resource-and-metadata-management/add-documentation/templates'
							}
							target="_blank"
							variant="tertiary"
							size="sm"
							rightIconName="externalLink"
						>
							Documentation
						</Button>
					</Flex>
					<Text color="text/secondary/default" size="sm">
						Create, manage, and organize templates for consistent documentation
						across your workspace. Templates are accessible to all members in
						your workspace.
					</Text>
				</Box>
				<Stack spacing="md">
					<Group w="100%" spacing="xs" noWrap>
						<SearchBox
							onSearch={setSearchTerm}
							onCancelSearch={() => setSearchTerm('')}
							placeholder="Search templates"
							size="xs"
						/>
						<Selector
							initialValue={[filter]}
							getItems={() => Promise.resolve(filterTemplateTypes)}
							onChange={(value) => setFilter(value[0] as 'all' | EntityType)}
							selectType={SelectType.Single}
							disableSearch
						>
							<SelectorTarget variant="default" size="lg">
								{
									filterTemplateTypes.find((item) => item.value === filter)
										?.label
								}
							</SelectorTarget>
						</Selector>
						<Menu
							opened={isOpen}
							onChange={onToggle}
							position="bottom-end"
							withinPortal
							data-testid="create-template-menu"
						>
							<Menu.Target>
								<Button
									variant="primary"
									size="lg"
									onClick={onToggle}
									rightIconName="chevronDown"
								>
									Create template
								</Button>
							</Menu.Target>
							<Menu.Dropdown>
								{createTemplateTypes?.map((type) => (
									<Menu.Item
										onClick={() => {
											setType(type.value);
											setTemplateId('');
											navigate('manage');
										}}
										key={type.value}
										icon={
											<Icon
												name={DEFAULT_TEMPLATE_ICONS[type.value] || 'file'}
											/>
										}
										data-testid={`create-${type.value}-template`}
									>
										{type.label}
									</Menu.Item>
								))}
							</Menu.Dropdown>
						</Menu>
					</Group>

					<Box ref={tableRef}>
						<DataTable<ITemplate>
							// Styling
							withStickyColumnBorder={false}
							noHeader={false}
							withBorder
							borderRadius="md"
							maxHeight={tableMaxHeight}
							height={tableMaxHeight}
							classNames={tableClasses}
							textSelectionDisabled={true}
							// Row click handler
							onRowClick={(row) => {
								setTemplateId(row.id);
								setType(row.entity_type_template);
								navigate('manage');
							}}
							// Data
							columns={[
								{
									accessor: 'title',
									title: 'Name',
									width: '350px',
									render: (row) => (
										<Group spacing="sm" noWrap>
											<UnstyledButton className={classes.icon}>
												<Icon
													name={
														DEFAULT_TEMPLATE_ICONS[row.entity_type_template] ||
														'file'
													}
												/>
											</UnstyledButton>
											<Stack spacing={0} miw={0}>
												<Text size="sm" truncate>
													{row.name}
												</Text>
												{row.is_default && (
													<Text size="xs" color="text/secondary/default">
														Default template
													</Text>
												)}
											</Stack>
										</Group>
									),
								},
								{
									accessor: 'entity_type_template',
									title: 'Type',
									render: (row) => (
										<Text size="sm">
											{getEntityDisplayType(row.entity_type_template)}
										</Text>
									),
								},
								{
									accessor: 'teams',
									title: 'Team Access',
									render: (row) => (
										<div onClick={(e) => e.stopPropagation()}>
											<TeamsTemplateSelector
												selected={row.usable_by_teams ?? []}
												handleChange={(value) => handleTeamChange(row, value)}
											/>
										</div>
									),
								},
								{
									accessor: 'actions',
									title: '',
									textAlignment: 'right',
									width: 50,
									render: (row) => (
										<Menu withinPortal>
											<Menu.Target>
												<ActionIcon
													size="sm"
													onClick={(e) => e.stopPropagation()}
												>
													<Icon name="dots" color="icon/secondary/default" />
												</ActionIcon>
											</Menu.Target>

											<Menu.Dropdown>
												<Menu.Item
													icon={
														<Icon
															name={row.is_default ? 'starFilled' : 'star'}
														/>
													}
													onClick={(e) => {
														e.stopPropagation();
														handleDefaultChange(row);
													}}
												>
													{row.is_default
														? 'Remove as default'
														: 'Set as default'}
												</Menu.Item>
												<Menu.Item
													icon={<Icon name="trash" />}
													onClick={(e) => {
														e.stopPropagation();
														openDeleteConfirmModal({
															title: 'Delete template?',
															description: "You can't undo this action.",
															onConfirm: () =>
																handleDeleteTemplate({ id: row.id }),
														});
													}}
												>
													Delete...
												</Menu.Item>
											</Menu.Dropdown>
										</Menu>
									),
								},
							]}
							records={templates || []}
						/>
					</Box>
				</Stack>
			</>
		);
	}
);
